<template>
  <div class="store-wechat-profile">
    <div class="base_content">
      <div class="page-head">
        <div>
          <div class="page-title">我的收款/分账状态</div>
          <div class="base_tips mt5">查看本门店微信收款商户、支付就绪和分账关系状态。</div>
        </div>
        <div>
          <el-button size="small" icon="el-icon-back" @click="$router.back()">返回</el-button>
          <el-button size="small" type="primary" @click="syncStatus">同步状态</el-button>
          <el-button size="small" @click="openConfirm">确认二维码</el-button>
          <el-button size="small" @click="syncConfirm">同步确认</el-button>
          <el-button size="small" type="success" @click="checkReady">支付检查</el-button>
          <el-button
            v-if="canMarkProfileAvailable"
            size="small"
            type="warning"
            :loading="markingProfile"
            @click="markProfileAvailable"
          >
            标记为可绑定
          </el-button>
          <el-button
            v-if="canSetCurrentBinding"
            size="small"
            type="primary"
            :loading="bindingSaving"
            @click="setCurrentBinding"
          >
            设为当前收款
          </el-button>
        </div>
      </div>
    </div>

    <div class="base_content mt10" v-loading="loading">
      <wechat-applyment-status-panel
        :applyment="applyment"
        :profile="profile"
        :block-reason="enableBlockReason"
      />
      <div class="section-title">收款商户档案</div>
      <div class="info-grid">
        <div class="info-item"><span>门店</span>{{ storeName }}</div>
        <div class="info-item"><span>特约商户号</span>{{ profile.sub_mchid || applyment.sub_mchid || "-" }}</div>
        <div class="info-item"><span>小程序 AppID</span>{{ profile.sub_appid || "-" }}</div>
        <div class="info-item"><span>申请状态</span>{{ applymentStatusText(definedValue(applyment.status, profile.applyment_status)) }}</div>
        <div class="info-item"><span>签约状态</span>{{ Number(profile.sign_status) === 50 ? "已签约" : "未完成" }}</div>
        <div class="info-item"><span>商户确认</span>{{ accountConfirmText(definedValue(applyment.account_confirm_status, profile.account_confirm_status)) }}</div>
        <div class="info-item"><span>小程序 AppID 状态</span>{{ Number(profile.appid_config_status) === 20 ? "已配置" : "未完成" }}</div>
        <div class="info-item"><span>JSAPI授权目录</span>{{ Number(profile.jsapi_path_status) === 0 ? "无需配置" : "待配置" }}</div>
        <div class="info-item"><span>分账关系</span>{{ Number(profile.profit_sharing_status) === 50 ? "已同步" : "未同步" }}</div>
        <div class="info-item"><span>最大分账比例</span>{{ ratioText(profile.max_profit_sharing_ratio) }}</div>
        <div class="info-item"><span>档案状态</span>{{ profileEnableStatus.text }}</div>
        <div class="info-item"><span>当前收款商户</span>{{ currentMerchantDisplay }}</div>
        <div class="info-item"><span>最后同步</span>{{ profile.last_sync_at || "-" }}</div>
      </div>

      <el-alert
        v-if="applyment.reject_reason"
        class="mt15"
        type="error"
        :closable="false"
        :title="applyment.reject_reason"
      />
    </div>

    <el-dialog title="商户确认二维码" :visible.sync="confirmDialog.show" width="420px">
      <div v-if="confirmDialog.url" class="confirm-box">
        <img v-if="confirmDialog.qr" :src="confirmDialog.qr" class="confirm-qr" alt="确认二维码" />
        <el-input v-model="confirmDialog.url" readonly size="small">
          <el-button slot="append" @click="copyConfirmUrl">复制</el-button>
        </el-input>
        <div class="base_tips mt10">请商户负责人使用微信扫码完成确认。确认完成后点击同步确认状态。</div>
      </div>
      <el-alert v-else type="warning" :closable="false" title="微信暂未返回确认链接，请先同步状态。" />
    </el-dialog>
  </div>
</template>

<script>
import QR from "@/components/common/wxqrcode.js";
import { eltips } from "@/util/util.js";
import WechatApplymentStatusPanel from "../../merchants/setting/wechat/components/WechatApplymentStatusPanel.vue";
import {
  checkStoreProfileReadiness,
  enableStoreProfile,
  getStoreApplyment,
  syncStoreApplyment,
  syncStoreApplymentConfirm,
} from "@/api/weishop/wechatApplyment";
import {
  enableStoreMerchantBinding,
  getStoreMerchantBinding,
} from "@/api/weishop/wechatStoreMerchantBinding";

const {
  shouldShowMerchantProfileEnableFallback,
} = require("../../merchants/setting/wechat/utils/wechatMerchantProfile");

export default {
  components: {
    WechatApplymentStatusPanel,
  },
  data() {
    return {
      loading: false,
      applyment: {},
      profile: {},
      currentBinding: {},
      markingProfile: false,
      bindingSaving: false,
      confirmDialog: {
        show: false,
        url: "",
        qr: "",
      },
    };
  },
  computed: {
    storeName() {
      const id = Number(this.definedValue(this.applyment.store_id, this.definedValue(this.profile.store_id, 0)));
      return id === 0 ? "平台" : `门店 #${id}`;
    },
    currentBindingProfile() {
      return (this.currentBinding && this.currentBinding.profile) || {};
    },
    currentMerchantDisplay() {
      const name = this.currentBindingProfile.merchant_name || this.currentBindingProfile.name || "";
      const mchid = this.currentBindingProfile.sub_mchid || this.currentBinding.sub_mchid || "";
      if (!name && !mchid) {
        return "未配置";
      }

      return [name || "微信商户", mchid].filter(Boolean).join(" / ");
    },
    isCurrentPaymentProfile() {
      const profileId = Number(this.profile.id || 0);
      const bindingProfileId = Number(this.currentBinding.merchant_profile_id || this.currentBindingProfile.id || 0);
      if (profileId > 0 && bindingProfileId > 0) {
        return profileId === bindingProfileId;
      }

      const profileSubMchid = String(this.profile.sub_mchid || this.applyment.sub_mchid || "");
      const bindingSubMchid = String(this.currentBinding.sub_mchid || this.currentBindingProfile.sub_mchid || "");
      return !!(profileSubMchid && bindingSubMchid && profileSubMchid === bindingSubMchid);
    },
    profileReady() {
      const explicitReady = this.definedValue(this.profile.is_payment_ready, "");
      if (explicitReady !== "") {
        return Number(explicitReady) === 1;
      }

      return Number(this.profile.is_enabled) === 1 &&
        String(this.profile.sub_mchid || "") !== "" &&
        Number(this.profile.applyment_status) === 50 &&
        Number(this.profile.sign_status) === 50 &&
        [0, 20].includes(Number(this.profile.account_confirm_status)) &&
        [0, 20].includes(Number(this.profile.jsapi_path_status)) &&
        Number(this.profile.profit_sharing_status) === 50;
    },
    profileReadinessReady() {
      const explicitStatus = this.definedValue(
        this.profile.payment_ready_status,
        this.definedValue(this.profile.readiness_status, "")
      );
      if (explicitStatus !== "") {
        return [1, 50].includes(Number(explicitStatus));
      }

      return String(this.profile.sub_mchid || "") !== "" &&
        Number(this.profile.applyment_status) === 50 &&
        Number(this.profile.sign_status) === 50 &&
        [0, 20].includes(Number(this.profile.account_confirm_status)) &&
        [0, 20].includes(Number(this.profile.jsapi_path_status)) &&
        Number(this.profile.profit_sharing_status) === 50;
    },
    profileEnableStatus() {
      if (Number(this.profile.is_enabled) === 1) {
        return { text: "可绑定" };
      }

      if (this.profileReadinessReady) {
        return { text: "待标记" };
      }

      return { text: "待检查" };
    },
    canMarkProfileAvailable() {
      return shouldShowMerchantProfileEnableFallback(
        this.profile,
        this.profileReadinessReady,
        this.profileReady
      );
    },
    canSetCurrentBinding() {
      return !!this.profile.id && this.profileReady && !this.isCurrentPaymentProfile;
    },
    enableBlockReason() {
      if (!this.profile.id) {
        return "请先同步生成收款商户档案。";
      }

      if (Number(this.profile.is_enabled) === 1) {
        return "";
      }

      const confirmStatus = Number(this.definedValue(this.applyment.account_confirm_status, this.profile.account_confirm_status));
      if (confirmStatus === 10 || confirmStatus === 40) {
        return "请先完成商户负责人微信确认。";
      }

      if (Number(this.profile.profit_sharing_status) !== 50) {
        return "请先完成微信分账接收方同步。";
      }

      return "支付就绪检查通过后系统会自动标记为可绑定；再设为当前收款。";
    },
  },
  created() {
    this.loadDetail();
    this.loadCurrentBinding();
  },
  methods: {
    isOk(res) {
      return res && (res.code === 200 || res.status === 200);
    },
    msg(res, fallback) {
      return (res && (res.msg || res.message)) || fallback;
    },
    definedValue(value, fallback) {
      return value === undefined || value === null ? fallback : value;
    },
    loadDetail() {
      this.loading = true;
      getStoreApplyment()
        .then((res) => {
          if (this.isOk(res)) {
            this.applyment = res.data.applyment || {};
            this.profile = res.data.profile || {};
            this.currentBinding = res.data.current_binding || this.currentBinding || {};
          } else {
            eltips(this.msg(res, "获取档案失败"), "error");
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadCurrentBinding() {
      getStoreMerchantBinding().then((res) => {
        if (this.isOk(res)) {
          this.currentBinding = (res.data && res.data.binding) || {};
        }
      });
    },
    syncStatus() {
      syncStoreApplyment().then((res) => {
        if (this.isOk(res)) {
          eltips(this.msg(res, "同步成功"), "success");
          this.loadDetail();
        } else {
          eltips(this.msg(res, "同步失败"), "error");
        }
      });
    },
    syncConfirm() {
      syncStoreApplymentConfirm().then((res) => {
        if (this.isOk(res)) {
          eltips(this.msg(res, "同步成功"), "success");
          this.loadDetail();
        } else {
          eltips(this.msg(res, "同步失败"), "error");
        }
      });
    },
    checkReady() {
      checkStoreProfileReadiness().then((res) => {
        if (this.isOk(res)) {
          eltips(this.msg(res, "检查成功"), "success");
          this.profile = res.data.profile || this.profile;
          this.loadCurrentBinding();
        } else {
          eltips(this.msg(res, "检查失败"), "error");
        }
      });
    },
    markProfileAvailable() {
      if (!this.profile.id) return;
      this.markingProfile = true;
      enableStoreProfile(this.profile.id)
        .then((res) => {
          if (this.isOk(res)) {
            eltips(this.msg(res, "档案已标记为可绑定"), "success");
            this.loadDetail();
          } else {
            eltips(this.msg(res, "标记可绑定失败"), "error");
          }
        })
        .finally(() => {
          this.markingProfile = false;
        });
    },
    setCurrentBinding() {
      if (!this.profile.id) return;
      this.bindingSaving = true;
      enableStoreMerchantBinding({ profile_id: this.profile.id })
        .then((res) => {
          if (this.isOk(res)) {
            eltips(this.msg(res, "已设为当前收款商户"), "success");
            this.loadCurrentBinding();
          } else {
            eltips(this.msg(res, "设置当前收款失败"), "error");
          }
        })
        .finally(() => {
          this.bindingSaving = false;
        });
    },
    openConfirm() {
      const url = this.applyment.account_confirm_url || this.applyment.sign_url || "";
      this.confirmDialog = {
        show: true,
        url,
        qr: url && url.indexOf("data:image") === 0 ? url : url ? QR.createQrCodeImg(url, { size: 220 }) : "",
      };
    },
    copyConfirmUrl() {
      if (this.confirmDialog.url && navigator.clipboard) {
        navigator.clipboard.writeText(this.confirmDialog.url);
      }
      eltips("确认链接已复制", "success");
    },
    applymentStatusText(status) {
      const map = { 10: "草稿", 20: "已提交", 30: "审核中", 40: "待确认/签约", 50: "已完成", 60: "已驳回" };
      return map[Number(status)] || "未知";
    },
    accountConfirmText(status) {
      const map = { 0: "无需确认", 10: "待确认", 20: "已确认" };
      return map[Number(status)] || "未确认";
    },
    ratioText(value) {
      if (value === undefined || value === null || value === "") return "-";
      return `${(Number(value) * 100).toFixed(2)}%`;
    },
  },
};
</script>

<style lang="less" scoped>
.store-wechat-profile {
  .page-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .page-title {
    color: #303133;
    font-size: 18px;
    font-weight: 600;
  }

  .section-title {
    margin-top: 16px;
    margin-bottom: 12px;
    color: #303133;
    font-size: 16px;
    font-weight: 600;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(260px, 1fr));
    border-top: 1px solid #ebeef5;
    border-left: 1px solid #ebeef5;
  }

  .info-item {
    min-height: 42px;
    padding: 11px 12px;
    border-right: 1px solid #ebeef5;
    border-bottom: 1px solid #ebeef5;
    color: #303133;

    span {
      display: inline-block;
      width: 120px;
      color: #909399;
    }
  }

  .confirm-box {
    text-align: center;
  }

  .confirm-qr {
    display: block;
    width: 220px;
    height: 220px;
    margin: 0 auto 16px;
  }
}
</style>
